import { useMemo } from 'react';
import { ParentResolution, PluginMessageTypes } from '../models/constants';
import { getUrlParams } from '../utils/navigation';
import usePluginReducer from './usePluginReducer';
import Log from '../utils/log';

// Funzione utilizzata per passare la dimensione della windows via querystring al children
export const getWindowSizeToParam = (w = window) => `${w.innerWidth}_${w.innerHeight}`;

const getWindowSize = () => ({ width: window.innerWidth, height: window.innerHeight });

// Funzione che legge le dimensioni passate via querystring da un parent
export const getWindowSizeFromParam = (param) => {
  const size = (param || getWindowSizeToParam()).split('_');
  if (size.length === 2) {
    const width = parseInt(size[0], 10);
    const height = parseInt(size[1], 10);
    if (!isNaN(width) && !isNaN(height)) {
      return { width, height };
    }
  }
  return getWindowSize();
};

const getInitResolution = () => {
  const parentResolution = getUrlParams()[ParentResolution];
  if (parentResolution) {
    return getWindowSizeFromParam(parentResolution);
  }
  Log.warn('resolution param non presente');
  return getWindowSize();
};

/**
Hook che consente di passare a ViewportProvider in container inizializzato con i valori forniti da url params.

__ATTENZIONE: se il padre non usa ViewportProvider anche il figlio potrebbe non ottenere la viewport corretta.
Specificare bene la dipendenza del plugin!__

I plugin children che volessero ricevere dal padre la viewport, dovrebbero creare una root come nell'esempio:

```
  ...
  const parentContainer = usePluginAppViewport();
  ...
  <ViewportProvider container={parentContainer}>
  </ViewportProvider>
```

poi

```
  function MyComponent(props) {
    ...
    const { resolution } = useViewport();
    ...
  }
```
 */
const usePluginAppViewport = (initResolution = getInitResolution()) => {
  // simula window come container del contesto ViewportProviders
  const parentContainer = useMemo(() => ({
    listeners: [],
    innerWidth: initResolution.width,
    innerHeight: initResolution.height,

    addEventListener(type, listener) {
      if (type === 'resize') this.listeners.push(listener);
    },

    removeEventListener(type, listener) {
      this.listeners = this.listeners.filter((l) => l !== listener);
    },

    notifyResolutionChanged(resolutionParam) {
      const resolution = getWindowSizeFromParam(resolutionParam);

      this.innerWidth = resolution.width;
      this.innerHeight = resolution.height;

      this.listeners.forEach((l) => l());
    },

  }), [initResolution]);

  usePluginReducer(() => ({
    [PluginMessageTypes.SET_RESOLUTION]: (resolution) => parentContainer.notifyResolutionChanged(resolution),
  }), [parentContainer]);

  return parentContainer;
};

export default usePluginAppViewport;
